import dayjs from "dayjs";
import {
  AvailabilityRequest,
  AvailabilityRequestEnum,
  AvailabilityResponse,
  IIdLodgings,
  getDepartureSlice,
  ILocationQueryLabel,
  IResponse,
  LocationQueryEnum,
  PlatformEnum,
  getReturnSlice,
  HotelItineraryResponse,
  HotelItineraryState,
  IResult,
  PRODUCT_FEED_NOT_RENDERED_EVENT,
  ProductFeedNotRenderedProperties,
  ProductFeedNotRenderedReason,
  BookedFlightItinerary as RedmondBookedFlightItinerary,
} from "redmond";
import { TrackEventRequest } from "@capone/platform-api";
import { put, select } from "redux-saga/effects";
import { fetchCrossSellHotelAvailability } from "../../../api/v0/cross-sell/fetchCrossSellHotelAvailability";
import { fetchHotels } from "../../../api/v0/cross-sell/fetchHotels";
import { fetchLodgingLocationAutocomplete } from "../../../api/v0/cross-sell/fetchLodgingLocationAutocomplete";
import Logger from "../../../helpers/Logger";
import { isMobile } from "../../../utils/userAgent";
import { actions } from "../actions";
import { getHotelAvailabilityNextPageToken } from "../reducer/selectors";
import { trackEvent } from "../../../api/v0/analytics/trackEvent";

const buildNotRenderedEvent = (
  reason: ProductFeedNotRenderedReason
): TrackEventRequest => ({
  eventName: PRODUCT_FEED_NOT_RENDERED_EVENT,
  properties: {
    reason,
    feed_type: "cross_sell",
    feed_placement: "booking_confirmation",
    feed_sub_placement: "below_fold",
    tile_type: "cross_sell_hotel",
    origin_product_type: "air",
    channel: isMobile() ? "Mobile" : "Web",
  } satisfies ProductFeedNotRenderedProperties,
});

export function* fetchCrossSellHotelAvailabilitySaga(
  action: actions.IFetchCrossSellHotelAvailability
) {
  try {
    const startTime = dayjs();
    let requestBody: AvailabilityRequest;
    const { bookedItinerary, airportMap } = action;
    switch (action.requestType) {
      case AvailabilityRequestEnum.InitialSearch: {
        const { prebooking } = action;

        let destinationCode: string;
        let destination: string;
        let departureDate: Date;
        let returnDate: Date;
        let adultPaxCount: number;
        let hasReturn: boolean;

        if (prebooking) {
          // Confirm-press prefetch: inputs derived from the selected trip.
          ({
            destinationCode,
            destination,
            departureDate,
            returnDate,
            adultPaxCount,
            hasReturn,
          } = prebooking);
        } else {
          if (!bookedItinerary || !airportMap) {
            trackEvent(buildNotRenderedEvent("no_booked_itinerary"));
            return;
          }

          const departureSlice = getDepartureSlice(
            bookedItinerary as unknown as RedmondBookedFlightItinerary
          ); // [TODO]: Fix type to used BookedFlightItinerary once it's fixed as a monorepo

          // Gets the last segment to avoid using the layover destinationCode
          const departureLastSegmentIndex = departureSlice
            ? departureSlice.segments.length - 1
            : 0;
          const departureDestinationSegment =
            departureSlice.segments[departureLastSegmentIndex];

          departureDate = new Date(
            departureDestinationSegment.zonedUpdatedArrival ??
            departureDestinationSegment.updatedArrival ??
            departureDestinationSegment.zonedScheduledArrival ??
            departureDestinationSegment.scheduledArrival
          );

          destinationCode =
            departureDestinationSegment?.destination.locationCode ?? "";
          destination =
            airportMap[destinationCode]?.cityName || destinationCode;

          const returnSlice = getReturnSlice(
            bookedItinerary as unknown as RedmondBookedFlightItinerary
          );
          hasReturn = returnSlice != null;

          returnDate = new Date(
            returnSlice?.segments[0].zonedUpdatedArrival ??
            returnSlice?.segments[0].updatedArrival ??
            returnSlice?.segments[0].zonedScheduledArrival ??
            returnSlice?.segments[0].scheduledArrival ??
            dayjs(departureDate).add(1, "day").toDate() // if one-way trip, look for hotel for 1 night
          );

          adultPaxCount = // if adult pax count is more than 2, default to 2
            bookedItinerary.passengers.alone.length +
            bookedItinerary.passengers.withLapInfants.length;
        }

        const departureDateWithoutTime =
          dayjs(departureDate).format("YYYY-MM-DD");
        // Since the checkin date does not have time, it's best to compare dates without time in the .find() below (incase departure time is later than the default 12AM GMT)
        const returnDateWithoutTime = dayjs(returnDate).format("YYYY-MM-DD");

        const hotels: HotelItineraryResponse = yield fetchHotels({
          states: [HotelItineraryState.Present, HotelItineraryState.Future], // Using departure date, look for present AND future (checkin date same as departure date or after)
          referenceDateTime: departureDate.toISOString(),
        });

        const { present: presentHotels = [], future: futureHotels = [] } =
          hotels.itineraries;

        const bookedHotel = [...presentHotels, ...futureHotels].find(
          (itinerary) => {
            const checkinDate = new Date(itinerary.reservation.checkInDate); // Defaults to 12AM GMT
            const formattedDepartureDateWithoutTime = new Date( // If we use normal departure date, if flight lands 1AM instead of 12AM, hotel would not be in range.
              departureDateWithoutTime
            );
            const formattedReturnDateWithoutTime = hasReturn
              ? new Date(returnDateWithoutTime)
              : new Date(
                dayjs(departureDateWithoutTime).add(2, "day").toDate()
              ); // if one-way trip, look for hotel booking made for within 2 days of departure
            return (
              checkinDate.valueOf() >=
              formattedDepartureDateWithoutTime.valueOf() &&
              checkinDate.valueOf() <= formattedReturnDateWithoutTime.valueOf()
            );
          }
        );

        if (!bookedHotel) {
          yield put(actions.setSearchedDates(departureDate, returnDate));

          const { correspondingLocation } = yield fetchLocation(destination);
          if (correspondingLocation == null) {
            // TODO(logging PR): instrument this bail (autocomplete resolved no location)
            yield put(actions.setHotelAvailabilityCallStateFailed());
            return;
          }
          yield put(actions.setSearchedLocationResult(correspondingLocation));
          yield put(
            actions.setSearchedOccupancyCounts({
              adults: adultPaxCount,
              children: [],
            })
          );

          requestBody = {
            lodgingSelection: (correspondingLocation.id as IIdLodgings)
              .lodgingSelection,
            stayDates: {
              from: departureDateWithoutTime,
              until: returnDateWithoutTime,
            },
            guests: {
              adults: adultPaxCount > 2 ? 2 : adultPaxCount,
              children: [],
            },
            platform: isMobile() ? PlatformEnum.Mobile : PlatformEnum.Desktop,
            progressiveConfig: {},
            AvailabilityRequest: AvailabilityRequestEnum.InitialSearch,
          };
        } else {
          trackEvent(buildNotRenderedEvent("already_has_hotel"));
          return;
        }
        break;
      }
      case AvailabilityRequestEnum.FollowUpSearch: {
        const nextPageToken: string | undefined = yield select(
          getHotelAvailabilityNextPageToken
        );

        if (!nextPageToken) {
          trackEvent(buildNotRenderedEvent("no_next_page_token"));
          return;
        }

        requestBody = {
          moreToken: nextPageToken,
          progressiveConfig: {},
          AvailabilityRequest: AvailabilityRequestEnum.FollowUpSearch,
        };
        break;
      }
      default:
        return;
    }

    const availabilityResponse: AvailabilityResponse =
      yield fetchCrossSellHotelAvailability(requestBody);

    if (action.requestType === AvailabilityRequestEnum.InitialSearch) {
      const { lodgings } = availabilityResponse;
      if (!lodgings?.length) {
        trackEvent(buildNotRenderedEvent("empty_availability"));
      } else if (lodgings.every((lodging) => lodging.available === false)) {
        // `available === undefined` we treat as available, only false is unavailable
        trackEvent(buildNotRenderedEvent("all_unavailable_filtered"));
      }
    }

    yield put(
      actions.setCrossSellHotelAvailabilityResults({
        payload: availabilityResponse,
        latency: dayjs().diff(startTime, "seconds", true),
      })
    );
  } catch (e) {
    yield put(actions.setHotelAvailabilityCallStateFailed());
    trackEvent(buildNotRenderedEvent("saga_exception"));
    Logger.debug(e);
  }
}

export function* fetchLocation(destination: string, countryCode?: string) {
  if (!destination) return { correspondingLocation: undefined };

  const allLocations: IResult[] = [];
  if (countryCode) {
    const locationRequestWithCountryCodeBody: ILocationQueryLabel = {
      LocationQuery: LocationQueryEnum.Label,
      l: `${destination}, ${countryCode}`,
    };
    const { categories: locationWithCountryCodeCategories }: IResponse =
      yield fetchLodgingLocationAutocomplete(
        locationRequestWithCountryCodeBody
      );

    allLocations.push(
      ...locationWithCountryCodeCategories.flatMap(
        (category) => category.results
      )
    );
  }

  const locationRequestBody: ILocationQueryLabel = {
    LocationQuery: LocationQueryEnum.Label,
    l: destination,
  };

  const { categories: locationCategories }: IResponse =
    yield fetchLodgingLocationAutocomplete(locationRequestBody);
  const nonCountryCodeLocations = locationCategories.flatMap(
    (category) => category.results
  );
  allLocations.push(...nonCountryCodeLocations);
  const matchingLocation =
    allLocations.find(
      (result) =>
        result.label.toLowerCase() === destination.toLowerCase() ||
        result.label.toLowerCase().startsWith(`${destination.toLowerCase()},`)
    ) ||
    allLocations.find((result) =>
      result.label.toLowerCase().includes(destination.toLowerCase())
    );

  const correspondingLocation = matchingLocation || nonCountryCodeLocations[0];

  return { correspondingLocation };
}